import React, { useState, useEffect, useContext } from "react";
import { _Button } from "@/components/button";
import { CheckCircleIcon, ChevronRightIcon, CircleXIcon, ShoppingBagIcon, LogInIcon, GlobeIcon, SignatureIcon, MailIcon, LockIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "@/hooks/form.hook";
import { TextFieldElement } from "@/components/formElements/text-field-element";
import { useUserService } from "@/hooks/user.hooks";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { RETRIEVE_ALL_TIKTOK_SELLER_ACCOUNTS, useTiktokSellerAccountService } from "@/hooks/tiktokSellerAccount.hooks";
import { MyAccountDropdown } from "@/components/my-account-dropdown";
import { useQueryClient } from "@tanstack/react-query";

export const LoggedInTikTokRedirect = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isAddingNewAccount, setIsAddingNewAccount] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { reAuth, addSellerAccount } = useTiktokSellerAccountService();
  const queryClient = useQueryClient();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    setAuthCode(code);

    if (state) {
      try {
        const decodedState = atob(state);
        const parsedState = JSON.parse(decodedState);
        if (parsedState.type === "ADD_SELLER_ACCOUNT") {
          setIsAddingNewAccount(true);
        }
      } catch (error) {
        console.error("Error parsing state:", error);
      }
    }
  }, [location]);

  const handleConfirm = async () => {
    setErrorMsg(null);
    setIsLoading(true);
    
    if (!authCode) {
      setErrorMsg("Authorization code is missing");
      setIsLoading(false);
      return;
    }

    try {
      const response = await (isAddingNewAccount ? addSellerAccount({
        authCode,
        isUS: false
      }) : reAuth({
        authCode,
        isUS: false
      }));

      setIsLoading(false);
      if (!response.success) {
        setErrorMsg(response.errorMessage || `An error occurred during ${isAddingNewAccount ? "adding" : "reauthorizing"} your TikTok Shop`);
        return;
      }

      queryClient.invalidateQueries({ queryKey: [RETRIEVE_ALL_TIKTOK_SELLER_ACCOUNTS] });
      navigate("/");
    } catch (error) {
      setIsLoading(false);
      setErrorMsg("An unexpected error occurred");
      console.error("Error during reauthorization:", error);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <div className="w-full bg-muted/10 border-b border-muted p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <p className="text-muted-foreground">You are currently logged in</p>
          <MyAccountDropdown />
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex flex-1">
        <div className="w-1/2 border-r flex items-center p-12">
          <div className="w-full space-y-8">
            <div>
              <h2 className="mt-6 text-4xl font-bold text-foreground">Welcome back to Mergoio</h2>
              <p className="mt-2 text-xl text-muted-foreground">
                {isAddingNewAccount ? "Add a new TikTok Shop" : "Reauthorize your TikTok Shop"}
              </p>
            </div>
            <div className="space-y-6">
              <div className="bg-muted/10 p-6 rounded-2xl shadow-sm border border-muted">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-500/30 rounded-full p-3">
                    <CheckCircleIcon className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground">Nearly finished!</h3>
                    <p className="text-lg text-muted-foreground">
                      {isAddingNewAccount 
                        ? "Your new seller account is ready to be added" 
                        : "Your seller account is ready to be reauthorized"}
                    </p>
                  </div>
                </div>
                <div className="mt-4 bg-background rounded-xl p-4 border border-muted">
                  <p className="text-xl text-blue-500">Click confirm on the right to complete the {isAddingNewAccount ? "addition" : "reauthorization"} process.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-1/2 bg-muted/40 flex items-center p-12">
          <div className="w-full space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-foreground">
                {isAddingNewAccount ? "Add New Account" : "Reauthorize Access"}
              </h2>
              <p className="mt-2 text-lg text-muted-foreground">
                Click confirm to {isAddingNewAccount ? "add your new TikTok Shop connection" : "reauthorize your TikTok Shop connection"}
              </p>
            </div>

            {errorMsg && (
              <div className="bg-destructive/10 rounded-xl px-4 py-3 flex gap-2 items-center text-destructive border border-destructive/20">
                <CircleXIcon size={16} />
                {errorMsg}
              </div>
            )}

            <div className="mt-8">
              <_Button
                onClick={handleConfirm}
                loading={isLoading}
                className="text-white h-14 group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-lg font-medium rounded-xl bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
              >
                {isAddingNewAccount ? "Confirm New Seller Account" : "Confirm Reauthorization"}
                <ChevronRightIcon className="ml-2 h-6 w-6 opacity-75 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
              </_Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
